import { Camera, Mail, Shield, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';

export default function ProfileSettings() {
  const { t } = useTranslation();
  const { user } = useAuth();

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2 text-gray-900">
          <User className="w-7 h-7 text-gray-700" />
          {t('profile.title')}
        </h1>
        <p className="text-gray-500 mt-1">{t('profile.description')}</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-100">
        {/* Personal Info */}
        <div className="p-6 space-y-4">
          <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <User className="w-5 h-5 text-gray-400" />
            {t('profile.personalInfo')}
          </h2>
          <div>
            <label htmlFor="profile-name" className="block text-sm font-medium text-gray-700 mb-1">
              {t('profile.name')}
            </label>
            <div className="flex items-center gap-3">
              <input
                id="profile-name"
                type="text"
                value={user?.name || ''}
                readOnly
                className="block w-full border-gray-300 rounded-md bg-gray-50 px-3 py-2 border text-gray-600 cursor-not-allowed"
              />
            </div>
          </div>
          <div>
            <label htmlFor="profile-email" className="block text-sm font-medium text-gray-700 mb-1">
              {t('profile.email')}
            </label>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-gray-400" />
              <input
                id="profile-email"
                type="email"
                value={user?.email || ''}
                readOnly
                className="block w-full border-gray-300 rounded-md bg-gray-50 px-3 py-2 border text-gray-600 cursor-not-allowed"
              />
            </div>
          </div>
          <div>
            <div className="block text-sm font-medium text-gray-700 mb-1">{t('profile.role')}</div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-gray-400" />
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {t(`auth.roles.${user?.role}`)}
              </span>
            </div>
          </div>
        </div>

        {/* Icon Settings (Integrated) */}
        <div className="p-6 space-y-4">
          <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <Camera className="w-5 h-5 text-gray-400" />
            {t('profile.iconSettings', 'アイコン設定')}
          </h2>
          <div className="flex items-center gap-6">
            <div className="h-20 w-20 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border border-gray-300">
              {/* Placeholder for user icon */}
              <User className="h-10 w-10 text-gray-400" />
            </div>
            <div className="flex-1">
              <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-6">
                <div className="text-center">
                  <Camera className="mx-auto h-8 w-8 text-gray-300" aria-hidden="true" />
                  <div className="mt-2 flex text-sm leading-6 text-gray-600 justify-center">
                    <label
                      htmlFor="file-upload"
                      className="relative cursor-pointer rounded-md bg-white font-semibold text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 hover:text-blue-500"
                    >
                      <span>Upload a file</span>
                      <input id="file-upload" name="file-upload" type="file" className="sr-only" />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs leading-5 text-gray-600">PNG, JPG, GIF up to 5MB</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
